import { NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { LATEST_VERSION, SEEN_KEY } from "../data/changelog";
import {
  Terminal,
  SquaresFour,
  Package,
  FolderSimple,
  SignOut,
  Lightning,
  Stack,
  Printer,
  UserCircleGear,
  SlidersHorizontal,
  Snowflake,
  Broadcast,
  Gear,
  BookOpen,
  UsersThree,
  UploadSimple,
  Robot,
  Cube,
  HardDrives,
  Sparkle,
  Bell,
} from "@phosphor-icons/react";

const navItems = [
  { to: "/app", label: "Generator", icon: Terminal, testid: "nav-generator", end: true },
  { to: "/app/templates", label: "Templates", icon: Stack, testid: "nav-templates" },
  { to: "/app/printers", label: "Printers", icon: Printer, testid: "nav-printers" },
  { to: "/app/sso", label: "SSO / Login", icon: UserCircleGear, testid: "nav-sso" },
  { to: "/app/appconfig", label: "Deploy Software", icon: SlidersHorizontal, testid: "nav-appconfig" },
  { to: "/app/custom-software", label: "Custom Software", icon: UploadSimple, testid: "nav-custom-software" },
  { to: "/app/autopkg", label: "AutoPkg Autopilot", icon: Robot, testid: "nav-autopkg" },
  { to: "/app/forgepkg", label: "ForgePkg", icon: Cube, testid: "nav-forgepkg" },
  { to: "/app/menubar", label: "MenuBar Forge", icon: Snowflake, testid: "nav-menubar" },
  { to: "/app/remote-desktop", label: "Remote Desktop", icon: Broadcast, testid: "nav-remote-desktop" },
  { to: "/app/catalog", label: "Software Catalog", icon: Package, testid: "nav-catalog" },
  { to: "/app/projects", label: "My Projects", icon: FolderSimple, testid: "nav-projects" },
  { to: "/app/knowledge", label: "Knowledge", icon: BookOpen, testid: "nav-knowledge" },
  { to: "/app/whats-new", label: "What's New", icon: Sparkle, testid: "nav-whatsnew" },
  { to: "/app/settings", label: "Settings", icon: Gear, testid: "nav-settings" },
];

const adminNavItems = [
  { to: "/app/admin", label: "User Management", icon: UsersThree, testid: "nav-admin", end: true },
  { to: "/app/packages", label: "Package Library", icon: HardDrives, testid: "nav-packages" },
];

function WhatsNewBell() {
  const navigate = useNavigate();
  const [unseen, setUnseen] = useState(() => localStorage.getItem(SEEN_KEY) !== LATEST_VERSION);
  useEffect(() => {
    const h = () => setUnseen(localStorage.getItem(SEEN_KEY) !== LATEST_VERSION);
    window.addEventListener("whatsnew-seen", h);
    return () => window.removeEventListener("whatsnew-seen", h);
  }, []);
  return (
    <button
      data-testid="whatsnew-bell"
      onClick={() => navigate("/app/whats-new")}
      title={unseen ? "New updates available" : "What's new"}
      className="relative w-8 h-8 shrink-0 border border-[#27272A] flex items-center justify-center text-[#A1A1AA] hover:text-[#00F0FF] hover:border-[#00F0FF] transition-colors duration-200"
    >
      <Bell size={16} weight={unseen ? "fill" : "regular"} />
      {unseen && (
        <span data-testid="whatsnew-dot" className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#00FF41] border border-[#0A0A0B]" />
      )}
    </button>
  );
}

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#0A0A0B] grain">
      <aside className="w-64 shrink-0 border-r border-[#27272A] bg-[#0A0A0B] flex flex-col fixed h-screen z-10">
        <div className="px-6 py-6 border-b border-[#27272A]">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 border border-[#00F0FF] flex items-center justify-center">
                <Lightning size={18} weight="fill" className="text-[#00F0FF]" />
              </div>
              <div>
                <div className="font-semibold tracking-tight text-[15px] leading-none">MDM Forge</div>
                <div className="font-mono text-[10px] text-[#71717A] mt-1">apple · plug &amp; play</div>
              </div>
            </div>
            <WhatsNewBell />
          </div>
        </div>

        <nav className="flex-1 py-4">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-sm transition-colors duration-200 border-l-2 ${
                  isActive
                    ? "border-[#00F0FF] text-[#FAFAFA] bg-[#121214]"
                    : "border-transparent text-[#A1A1AA] hover:text-[#FAFAFA] hover:bg-[#121214]"
                }`
              }
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <>
              <div className="px-6 pt-4 pb-1 font-mono text-[9px] uppercase tracking-wider text-[#52525B]">
                Admin
              </div>
              {adminNavItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={item.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-6 py-3 text-sm transition-colors duration-200 border-l-2 ${
                      isActive
                        ? "border-[#00F0FF] text-[#FAFAFA] bg-[#121214]"
                        : "border-transparent text-[#A1A1AA] hover:text-[#FAFAFA] hover:bg-[#121214]"
                    }`
                  }
                >
                  <item.icon size={18} />
                  {item.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div className="border-t border-[#27272A] p-4">
          <div className="flex items-center gap-3 px-2 py-2 mb-2">
            <div className="w-8 h-8 border border-[#27272A] flex items-center justify-center font-mono text-xs text-[#00F0FF]">
              {(user?.name || user?.email || "U").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-xs text-[#FAFAFA] truncate">{user?.name || "User"}</div>
              <div className="font-mono text-[10px] text-[#71717A] truncate">{user?.email}</div>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#A1A1AA] border border-[#27272A] hover:text-[#FF5F56] hover:border-[#FF5F56] transition-colors duration-200"
          >
            <SignOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 ml-64 relative z-[2]">{children}</main>
    </div>
  );
}
